/*
 * benvel-wishlist — Story 14.1.
 *
 * Persistence + toggle + analytics for the WishlistHeartButton primitive.
 *
 * Persistence v1: localStorage only. Storage shape per
 * `design/contracts/wishlist.md` § Persistence v1 (when authored by 13.2):
 *   [{ productId, variantId, color, size, savedAt, sourcePage }]
 * The v1.5 customer-metafield mirror is documented in the same contract and
 * lives in BU asks (§A.7 / §A.8 / §B.1). Not shipped here.
 *
 * Public API (namespaced under window.benvel — Stories 8.1 / 11.4a precedent):
 *   window.benvel.wishlist.getProducts()
 *   window.benvel.wishlist.isSaved(productId, variantId?)
 *   window.benvel.wishlist.toggle({ productId, variantId, color, size, sourcePage })
 *
 * Analytics: dispatches `benvel:wishlist-saved` ONLY when
 *   window.benvel.consent?.has?.('analytics') === true.
 * Payload contains opaque IDs only — no PII per Story 8.1 denylist.
 *
 * Toast: emits `benvel:wishlist-toast` for any consumer toast primitive
 * (no existing canonical toast snippet — Story 14.1 inlines minimal toast
 * DOM on the fly; future extraction is tracked in Implementation notes).
 *
 * Header pulse: emits `benvel:wishlist-header-pulse`. If no header heart
 * exists yet (verified at 2026-05-22), the event simply has no listener —
 * AC3 NO-OP path documented in story Dev Notes (Story 14.2 wires the
 * count badge + listener).
 */

(function () {
  "use strict";

  // Idempotent guard — the asset may be referenced multiple times on a
  // single page when many product-card snippets each emit a tag. Browsers
  // dedupe by URL but the IIFE could still attach duplicate listeners; we
  // explicitly bail on second load.
  if (window.benvel && window.benvel.wishlist && window.benvel.wishlist.__loaded) {
    return;
  }

  var STORAGE_KEY = "benvel.wishlist.products";
  var TOAST_LIFETIME_MS = 3600;
  var HEADER_PULSE_DURATION_MS = 600;

  // Story A5-S3 — server merge + debounced app-proxy sync constants.
  // The endpoint is served by the BU app proxy (Story A5-S1). Until S1
  // ships, every POST 404s — handled fail-soft below (local state kept,
  // retry on next change, zero user-facing errors).
  var SYNC_ENDPOINT = "/apps/benvel/wishlist-sync";
  var SYNC_DEBOUNCE_MS = 2000;
  var SYNC_MAX_ITEMS = 200;

  // ──────────────────────────────────────────────────────────────────────
  // Storage helpers
  // ──────────────────────────────────────────────────────────────────────

  function readStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_err) {
      // Quota exceeded, JSON parse error, or storage unavailable (private mode).
      // Silent fall-back to empty list per NFR no-JS survival posture.
      return [];
    }
  }

  function writeStorage(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      return true;
    } catch (_err) {
      return false;
    }
  }

  function matches(entry, productId, variantId) {
    if (!entry) return false;
    if (String(entry.productId) !== String(productId)) return false;
    if (variantId == null) return true;
    return String(entry.variantId) === String(variantId);
  }

  function findIndex(list, productId, variantId) {
    for (var i = 0; i < list.length; i += 1) {
      if (matches(list[i], productId, variantId)) return i;
    }
    return -1;
  }

  // ──────────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────────

  function getProducts() {
    return readStorage();
  }

  function isSaved(productId, variantId) {
    return findIndex(readStorage(), productId, variantId) !== -1;
  }

  function toggle(input) {
    var payload = input || {};
    var productId = payload.productId;
    if (productId == null) return false;
    var variantId = payload.variantId == null ? null : payload.variantId;
    var color = payload.color == null ? null : payload.color;
    var size = payload.size == null ? null : payload.size;
    var sourcePage = payload.sourcePage || "plp";

    var list = readStorage();
    var index = findIndex(list, productId, variantId);
    var nowSaved;

    if (index === -1) {
      list.push({
        productId: String(productId),
        variantId: variantId == null ? null : String(variantId),
        color: color,
        size: size,
        savedAt: new Date().toISOString(),
        sourcePage: sourcePage,
      });
      nowSaved = true;
    } else {
      list.splice(index, 1);
      nowSaved = false;
    }

    writeStorage(list);
    scheduleServerSync();

    if (nowSaved) {
      emitToast(payload);
      emitHeaderPulse();
      maybeEmitAnalytics({
        productId: String(productId),
        variantId: variantId == null ? null : String(variantId),
        sourcePage: sourcePage,
      });
    }

    syncButtonsFor(productId, variantId, nowSaved);
    return nowSaved;
  }

  // ──────────────────────────────────────────────────────────────────────
  // Consent-gated analytics
  // ──────────────────────────────────────────────────────────────────────

  function maybeEmitAnalytics(detail) {
    var consent =
      typeof window !== "undefined" && window.benvel
        ? window.benvel.consent
        : null;
    if (!consent || typeof consent.has !== "function") return; // silent skip
    if (consent.has("analytics") !== true) return; // silent skip
    // Payload: opaque IDs only — productId, variantId, sourcePage.
    // Story 8.1 PII denylist applies.
    document.dispatchEvent(
      new CustomEvent("benvel:wishlist-saved", {
        bubbles: true,
        detail: {
          productId: detail.productId,
          variantId: detail.variantId,
          sourcePage: detail.sourcePage,
        },
      })
    );
  }

  // ──────────────────────────────────────────────────────────────────────
  // Toast (inline minimal — no existing canonical snippet to reuse)
  // ──────────────────────────────────────────────────────────────────────

  function emitToast(payload) {
    document.dispatchEvent(
      new CustomEvent("benvel:wishlist-toast", {
        bubbles: true,
        detail: {
          productId: payload.productId,
          productTitle: payload.productTitle || null,
        },
      })
    );

    // Minimal in-DOM toast. Future canonical extraction: see
    // `_bmad-output/.../14-1-...` Implementation notes.
    var container = ensureToastContainer();
    if (!container) return;

    var toast = document.createElement("div");
    toast.className = "bv-wishlist-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.setAttribute("data-state", "entering");

    var title = document.createElement("p");
    title.className = "bv-wishlist-toast__title";
    title.textContent = container.getAttribute("data-i18n-title") || "Saved";

    var body = document.createElement("p");
    body.className = "bv-wishlist-toast__body";
    var bodyTemplate = container.getAttribute("data-i18n-body");
    if (bodyTemplate && payload.productTitle) {
      body.textContent = bodyTemplate.replace(
        "{{product_title}}",
        payload.productTitle
      );
    } else if (bodyTemplate) {
      body.textContent = bodyTemplate.replace("{{product_title}}", "");
    } else if (payload.productTitle) {
      body.textContent = payload.productTitle;
    }

    var dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.className = "bv-wishlist-toast__dismiss";
    dismiss.setAttribute(
      "aria-label",
      container.getAttribute("data-i18n-dismiss") || "Close"
    );
    dismiss.textContent = "×"; // × character
    dismiss.addEventListener("click", function () {
      removeToast(toast);
    });

    toast.appendChild(title);
    toast.appendChild(body);
    toast.appendChild(dismiss);

    container.appendChild(toast);

    // Force reflow then set visible state for CSS transition.
    // eslint-disable-next-line no-unused-expressions
    toast.offsetWidth;
    toast.setAttribute("data-state", "visible");

    window.setTimeout(function () {
      removeToast(toast);
    }, TOAST_LIFETIME_MS);
  }

  function removeToast(toast) {
    if (!toast || !toast.parentNode) return;
    toast.setAttribute("data-state", "leaving");
    window.setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 200);
  }

  function ensureToastContainer() {
    var existing = document.querySelector("[data-bv-wishlist-toast-region]");
    if (existing) return existing;
    var region = document.createElement("div");
    region.className = "bv-wishlist-toast-region";
    region.setAttribute("data-bv-wishlist-toast-region", "");
    region.setAttribute("aria-live", "polite");
    region.setAttribute("aria-atomic", "false");
    // Read i18n payload from a meta tag if present (rendered by theme.liquid
    // or by the wishlist-heart-button snippet on first hydration).
    var src = document.querySelector("[data-bv-wishlist-i18n]");
    if (src) {
      region.setAttribute(
        "data-i18n-title",
        src.getAttribute("data-toast-title") || ""
      );
      region.setAttribute(
        "data-i18n-body",
        src.getAttribute("data-toast-body") || ""
      );
      region.setAttribute(
        "data-i18n-dismiss",
        src.getAttribute("data-toast-dismiss") || ""
      );
    }
    document.body.appendChild(region);
    return region;
  }

  // ──────────────────────────────────────────────────────────────────────
  // Header pulse hook (NO-OP if no listener — Story 14.2 wires the badge)
  // ──────────────────────────────────────────────────────────────────────

  function emitHeaderPulse() {
    document.dispatchEvent(
      new CustomEvent("benvel:wishlist-header-pulse", {
        bubbles: true,
        detail: { durationMs: HEADER_PULSE_DURATION_MS },
      })
    );
  }

  // ──────────────────────────────────────────────────────────────────────
  // DOM wiring
  // ──────────────────────────────────────────────────────────────────────

  function syncButtonsFor(productId, variantId, saved) {
    var selector =
      '[data-wishlist-heart][data-product-id="' + String(productId) + '"]';
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      var nodeVariant = node.getAttribute("data-variant-id");
      if (
        variantId != null &&
        nodeVariant != null &&
        String(nodeVariant) !== String(variantId)
      ) {
        continue;
      }
      setButtonState(node, saved);
    }
  }

  function setButtonState(button, saved) {
    button.setAttribute("aria-pressed", saved ? "true" : "false");
    if (saved) {
      button.setAttribute("data-saved", "true");
      var ariaRemove = button.getAttribute("data-aria-remove");
      if (ariaRemove) button.setAttribute("aria-label", ariaRemove);
    } else {
      button.removeAttribute("data-saved");
      var ariaSave = button.getAttribute("data-aria-save");
      if (ariaSave) button.setAttribute("aria-label", ariaSave);
    }
  }

  function hydrateButton(button) {
    var productId = button.getAttribute("data-product-id");
    var variantId = button.getAttribute("data-variant-id");
    if (!productId) return;
    var saved = isSaved(productId, variantId);
    setButtonState(button, saved);
  }

  function onButtonClick(event) {
    var button = event.currentTarget;
    var productId = button.getAttribute("data-product-id");
    if (!productId) return;
    var variantId = button.getAttribute("data-variant-id");
    var sourcePage = button.getAttribute("data-source-page") || "plp";
    var productTitle = button.getAttribute("data-product-title") || null;
    toggle({
      productId: productId,
      variantId: variantId,
      color: null,
      size: null,
      sourcePage: sourcePage,
      productTitle: productTitle,
    });
  }

  function attachButton(button) {
    if (button.dataset.bvWishlistBound === "1") return;
    button.dataset.bvWishlistBound = "1";
    button.addEventListener("click", onButtonClick);
    hydrateButton(button);
  }

  function hydrateAll() {
    var nodes = document.querySelectorAll("[data-wishlist-heart]");
    for (var i = 0; i < nodes.length; i += 1) {
      attachButton(nodes[i]);
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Story A5-S3 — server merge + debounced app-proxy sync (logged-in only)
  //
  // Contract (spec-a5-build-stories-2026-07-20.md, "Shared data model"):
  //   - The Liquid carrier `<script type="application/json"
  //     data-benvel-wishlist-server>` is emitted by
  //     `snippets/wishlist-server-carrier.liquid` ONLY when a customer is
  //     logged in. Carrier absence ⇒ logged out ⇒ this whole block is
  //     inert (no fetch, no storage writes, no behavior change).
  //   - Union key: variantId; fallback productId+size+color.
  //   - Server wins on conflict. Hard cap 200 items, dropping oldest by
  //     savedAt.
  //   - On change: debounced ~2 s POST of the full local array to the app
  //     proxy. 200 ⇒ replace local state with the returned merged array.
  //     Network error / 4xx / 5xx ⇒ keep local state, retry on next
  //     change. NO user-facing errors ever.
  // ──────────────────────────────────────────────────────────────────────

  var serverCarrierEl; // memoized after first lookup

  function serverCarrier() {
    if (serverCarrierEl === undefined) {
      serverCarrierEl = document.querySelector(
        'script[type="application/json"][data-benvel-wishlist-server]'
      );
    }
    return serverCarrierEl;
  }

  function readServerList() {
    var el = serverCarrier();
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_err) {
      // Malformed carrier — fail-soft to empty per spec.
      return [];
    }
  }

  // Union key: variantId when present; fallback productId+size+color.
  // Returns null for entries with neither identity (dropped from merges).
  function unionKey(entry) {
    if (!entry || typeof entry !== "object") return null;
    if (entry.variantId != null && String(entry.variantId) !== "") {
      return "v:" + String(entry.variantId);
    }
    if (entry.productId == null) return null;
    return (
      "p:" +
      String(entry.productId) +
      "|" +
      (entry.size == null ? "" : String(entry.size)) +
      "|" +
      (entry.color == null ? "" : String(entry.color))
    );
  }

  function savedAtMs(entry) {
    var t = entry && entry.savedAt ? Date.parse(entry.savedAt) : NaN;
    return isNaN(t) ? 0 : t;
  }

  // Union-merge per the shared model: server wins on key conflict; entries
  // unique to either side are kept; cap SYNC_MAX_ITEMS dropping oldest by
  // savedAt. Unknown fields on entries are preserved as-is (superset
  // allowed).
  function mergeLists(serverList, localList) {
    var map = {};
    var order = [];
    var i, key, entry;
    for (i = 0; i < localList.length; i += 1) {
      entry = localList[i];
      key = unionKey(entry);
      if (key == null) continue;
      if (!Object.prototype.hasOwnProperty.call(map, key)) order.push(key);
      map[key] = entry;
    }
    for (i = 0; i < serverList.length; i += 1) {
      entry = serverList[i];
      key = unionKey(entry);
      if (key == null) continue;
      if (!Object.prototype.hasOwnProperty.call(map, key)) order.push(key);
      map[key] = entry; // server wins on conflict
    }
    var merged = [];
    for (i = 0; i < order.length; i += 1) {
      merged.push(map[order[i]]);
    }
    if (merged.length > SYNC_MAX_ITEMS) {
      merged.sort(function (a, b) {
        return savedAtMs(b) - savedAtMs(a); // newest first
      });
      merged = merged.slice(0, SYNC_MAX_ITEMS); // drop oldest by savedAt
    }
    return merged;
  }

  function sameList(a, b) {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (_err) {
      return false;
    }
  }

  function emitServerMerged() {
    try {
      document.dispatchEvent(
        new CustomEvent("benvel:wishlist-server-merged", { bubbles: true })
      );
    } catch (_err) {
      /* CustomEvent unavailable — silent. */
    }
  }

  // On-load merge (logged-in only). Server wins; if the local (possibly
  // anonymous, pre-login) list contributed anything the server doesn't
  // have, schedule a push so nothing is silently lost.
  function mergeFromServer() {
    if (!serverCarrier()) return; // logged out — inert
    var server = readServerList();
    var local = readStorage();
    if (server.length === 0 && local.length === 0) return;
    var merged = mergeLists(server, local);
    if (!sameList(merged, local)) {
      writeStorage(merged);
      hydrateAll();
      emitServerMerged();
    }
    if (!sameList(merged, server)) {
      scheduleServerSync();
    }
  }

  var syncTimer = null;

  function scheduleServerSync() {
    if (!serverCarrier()) return; // logged out — no fetch, ever
    if (typeof window.fetch !== "function") return;
    if (syncTimer != null) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(runServerSync, SYNC_DEBOUNCE_MS);
  }

  function runServerSync() {
    syncTimer = null;
    var list = readStorage();
    var body;
    try {
      body = JSON.stringify({ saved_products: list });
    } catch (_err) {
      return; // unserializable state — keep local, retry on next change
    }
    window
      .fetch(SYNC_ENDPOINT, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: body,
      })
      .then(function (res) {
        // 404 until Story A5-S1 deploys the proxy; 4xx/5xx otherwise —
        // keep local state, retry on next change. No user-facing errors.
        if (!res || !res.ok) return null;
        return res.json();
      })
      .then(function (payload) {
        if (!payload) return;
        // S1 responds `{ saved_products: [...] }` (merged state). Accept a
        // bare array too for forward compatibility.
        var serverList = Array.isArray(payload)
          ? payload
          : payload.saved_products;
        if (!Array.isArray(serverList)) return;
        if (serverList.length > SYNC_MAX_ITEMS) {
          serverList = mergeLists(serverList, []);
        }
        if (sameList(serverList, readStorage())) return;
        writeStorage(serverList); // 200 ⇒ server merged array replaces local
        hydrateAll();
        emitServerMerged();
      })
      .catch(function (_err) {
        /* Network failure — keep local state, retry on next change. */
      });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Boot
  // ──────────────────────────────────────────────────────────────────────

  window.benvel = window.benvel || {};
  window.benvel.wishlist = {
    getProducts: getProducts,
    isSaved: isSaved,
    toggle: toggle,
    __loaded: true,
  };

  function boot() {
    hydrateAll();
    mergeFromServer(); // A5-S3 — no-op when logged out (carrier absent)
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  // Re-hydrate on Shopify section-render events (PLP filter re-render, etc.).
  document.addEventListener("shopify:section:load", hydrateAll);
})();

/* ──────────────────────────────────────────────────────────────────────────
 * Story 14.3 — Wishlist toolbar submodule.
 *
 * Owns:
 *   - ChipGroup (Recientes / Disponibles / Por ocasión) — aria-pressed
 *     toggling + arrow-key roving focus + sessionStorage persistence
 *     (Decision §D6).
 *   - Active-tab awareness — "Disponibles" hides on Looks tab (looks have no
 *     v1 inventory state per Story 14.2 schema).
 *   - "Por ocasión" sub-picker — lightweight dropdown adjacent to the chip
 *     (Decision §D2). Falls back to "all occasions" when no occasions are
 *     present in the saved set.
 *   - Share button + intent picker dialog (3 intents per AC3). Composes
 *     wa.me URL using `settings.benvel_whatsapp_number` (passed via
 *     data-wa-number) + interpolated `wishlist.share.message_{intent}`
 *     locale string.
 *   - Consent-gated `benvel:wishlist-shared` analytics event (AC4) — payload
 *     is PII-free counts only (Decision §D5).
 *
 * Submodule does NOT own:
 *   - The Piezas/Looks tab control (Story 14.2 inline section JS).
 *   - The grid hydration / re-render (Story 14.2 owns the grid; the
 *     submodule dispatches `benvel:wishlist-filter-changed` on document and
 *     the page section listens — the page section was authored ahead of
 *     14.3 and intentionally renders its empty grid surrogate from the
 *     latest filter event when present; if the page section has no such
 *     listener at runtime, the chips still flip aria-pressed and persist
 *     the filter selection to sessionStorage. The event-only contract keeps
 *     the toolbar decoupled from grid implementation details).
 *
 * Asset stays under the 4 KB gz incremental budget — no external deps, no
 * new CDN, no third-party scripts. Pure native CustomEvent + DOM APIs.
 * ────────────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  if (
    window.benvel &&
    window.benvel.wishlistToolbar &&
    window.benvel.wishlistToolbar.__loaded
  ) {
    return;
  }

  var FILTER_STORAGE_KEY = "benvel.wishlist.filter";
  var DEFAULT_FILTER = "recientes";
  var VALID_FILTERS = ["recientes", "disponibles", "por-ocasion"];

  // ─── Helpers ───────────────────────────────────────────────────────────

  function safeReadProducts() {
    try {
      var raw = window.localStorage.getItem("benvel.wishlist.products");
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  function safeReadLooks() {
    try {
      var raw = window.localStorage.getItem("benvel.wishlist.looks");
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  function readPersistedFilter() {
    try {
      var raw = window.sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (!raw) return DEFAULT_FILTER;
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.filterId) {
        return VALID_FILTERS.indexOf(parsed.filterId) !== -1
          ? parsed.filterId
          : DEFAULT_FILTER;
      }
      return DEFAULT_FILTER;
    } catch (_e) {
      return DEFAULT_FILTER;
    }
  }

  function persistFilter(filterId, activeOccasion) {
    try {
      window.sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          filterId: filterId,
          activeOccasion: activeOccasion || null,
        })
      );
    } catch (_e) {
      /* Quota / private mode — fail silent, the in-memory state still drives the UI. */
    }
  }

  function hasAnalyticsConsent() {
    return !!(
      window.benvel &&
      window.benvel.consent &&
      typeof window.benvel.consent.has === "function" &&
      window.benvel.consent.has("analytics")
    );
  }

  function formatCOP(value) {
    if (typeof value !== "number" || isNaN(value)) value = 0;
    try {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }).format(value);
    } catch (_e) {
      return "$" + Math.round(value).toLocaleString("es-CO");
    }
  }

  // Apply current filter state to the in-memory product/look lists. Returns
  // { products, looks } — the toolbar submodule emits the event so other
  // listeners (Story 14.2 page section or future grid updaters) can consume
  // the filtered view. Today the page section listens via the
  // `storage`/`benvel:wishlist-*` events and re-runs its own hydrate(); the
  // filtered view is reconstructed here only for the share-message body.
  function applyFilter(products, looks, state, activeTab) {
    var filterId = state.filterId;
    var activeOccasion = state.activeOccasion;
    var p = products.slice();
    var l = looks.slice();

    if (filterId === "recientes") {
      // Sort by savedAt descending (Story 14.1 schema).
      p.sort(function (a, b) {
        var ta = a && a.savedAt ? Date.parse(a.savedAt) : 0;
        var tb = b && b.savedAt ? Date.parse(b.savedAt) : 0;
        return tb - ta;
      });
      l.sort(function (a, b) {
        var ta = a && a.savedAt ? Date.parse(a.savedAt) : 0;
        var tb = b && b.savedAt ? Date.parse(b.savedAt) : 0;
        return tb - ta;
      });
    } else if (filterId === "disponibles") {
      // Best-effort sync purchasable check (Decision §D7). Reads
      // window.benvel.wishlist.purchasableMap (an in-memory hint Story 14.2's
      // grid composition may populate). If absent, degrade to "show all" so
      // the chip never strands the buyer. Looks have no v1 inventory state.
      var pm =
        window.benvel &&
        window.benvel.wishlist &&
        window.benvel.wishlist.purchasableMap;
      if (pm && typeof pm === "object") {
        p = p.filter(function (entry) {
          var vid = entry && entry.variantId;
          if (vid == null) return true;
          var hit = pm[String(vid)];
          return hit === undefined ? true : !!hit;
        });
      }
      // Disponibles does NOT apply to Looks — leave l unfiltered; the chip
      // is hidden when Looks tab is active (see syncTabAwareness).
    } else if (filterId === "por-ocasion" && activeOccasion) {
      p = p.filter(function (entry) {
        return entry && entry.occasion === activeOccasion;
      });
      l = l.filter(function (entry) {
        return entry && entry.occasion === activeOccasion;
      });
    }

    return { products: p, looks: l };
  }

  function buildItemLines(t, products, looks) {
    var lines = [];
    products.forEach(function (entry) {
      if (!entry) return;
      var title = entry.title || entry.productTitle || "";
      if (!title) return;
      var size = entry.size;
      var color = entry.color;
      var line;
      if (size && color) {
        line = t.item_line
          .replace("{title}", title)
          .replace("{size}", size)
          .replace("{color}", color);
      } else if (color) {
        line = t.item_line_no_size
          .replace("{title}", title)
          .replace("{color}", color);
      } else {
        line = t.item_line_minimal.replace("{title}", title);
      }
      lines.push(line);
    });
    looks.forEach(function (look) {
      if (!look) return;
      var title = look.title || "Look";
      lines.push(t.item_line_minimal.replace("{title}", title));
    });
    return lines.join(" ");
  }

  function sumTotals(products, looks) {
    var total = 0;
    products.forEach(function (entry) {
      var v = entry && (entry.price || entry.totalPrice || 0);
      if (typeof v === "number" && !isNaN(v)) total += v;
    });
    looks.forEach(function (look) {
      var v = look && look.totalPrice;
      if (typeof v === "number" && !isNaN(v)) total += v;
    });
    return total;
  }

  // ─── Init ──────────────────────────────────────────────────────────────

  function init() {
    var root = document.querySelector("[data-bv-wishlist-toolbar]");
    if (!root) return;

    var chips = Array.prototype.slice.call(
      root.querySelectorAll("[data-filter-value]")
    );
    if (chips.length === 0) return;

    var state = {
      filterId: readPersistedFilter(),
      activeOccasion: null,
    };

    // Locale strings — pulled once from a carrier the snippet adds via t.
    // (We read them from the page section's rendered DOM where convenient;
    // here we pull message templates from a hidden carrier injected by
    // the toolbar's wrapper for clean separation. Fallback to chip labels
    // when missing.)
    var localeCarrier = root.querySelector("[data-bv-share-i18n]");
    var t = {
      message_amiga:
        (localeCarrier && localeCarrier.getAttribute("data-msg-amiga")) || "",
      message_asesoria:
        (localeCarrier && localeCarrier.getAttribute("data-msg-asesoria")) ||
        "",
      message_ayuda_con_calce:
        (localeCarrier &&
          localeCarrier.getAttribute("data-msg-ayuda-con-calce")) ||
        "",
      item_line:
        (localeCarrier && localeCarrier.getAttribute("data-item-line")) ||
        "• {title} — {size} • {color}",
      item_line_no_size:
        (localeCarrier &&
          localeCarrier.getAttribute("data-item-line-no-size")) ||
        "• {title} — {color}",
      item_line_minimal:
        (localeCarrier &&
          localeCarrier.getAttribute("data-item-line-minimal")) ||
        "• {title}",
      empty_message:
        (localeCarrier && localeCarrier.getAttribute("data-empty-message")) ||
        "",
    };

    // ─── Chip state ─────────────────────────────────────────────────────

    function setChipPressed(filterId) {
      chips.forEach(function (chip) {
        var v = chip.getAttribute("data-filter-value");
        chip.setAttribute("aria-pressed", v === filterId ? "true" : "false");
        chip.tabIndex = v === filterId ? 0 : -1;
      });
    }

    function emitFilterChanged() {
      try {
        document.dispatchEvent(
          new CustomEvent("benvel:wishlist-filter-changed", {
            bubbles: true,
            detail: {
              filterId: state.filterId,
              activeOccasion: state.activeOccasion,
            },
          })
        );
      } catch (_e) {
        /* IE11 fallback — not in scope. */
      }
    }

    function activateFilter(filterId, opts) {
      opts = opts || {};
      if (VALID_FILTERS.indexOf(filterId) === -1) return;
      state.filterId = filterId;
      if (filterId !== "por-ocasion") {
        state.activeOccasion = null;
        if (occasionPicker) occasionPicker.setAttribute("hidden", "");
      }
      setChipPressed(filterId);
      persistFilter(filterId, state.activeOccasion);
      emitFilterChanged();
      if (filterId === "por-ocasion" && opts.openPicker !== false) {
        openOccasionPicker();
      }
    }

    chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        activateFilter(chip.getAttribute("data-filter-value"));
      });
    });

    // Arrow-key roving focus (WAI-ARIA APG toggle button group).
    function onChipKeydown(event) {
      var key = event.key;
      if (
        key !== "ArrowLeft" &&
        key !== "ArrowRight" &&
        key !== "Home" &&
        key !== "End"
      ) {
        return;
      }
      // Honor [hidden] chips (the Looks tab hides "Disponibles").
      var visible = chips.filter(function (c) {
        return !c.hasAttribute("hidden");
      });
      if (visible.length === 0) return;
      event.preventDefault();
      var current = visible.indexOf(document.activeElement);
      if (current < 0) current = 0;
      var next = current;
      if (key === "ArrowLeft") next = (current - 1 + visible.length) % visible.length;
      if (key === "ArrowRight") next = (current + 1) % visible.length;
      if (key === "Home") next = 0;
      if (key === "End") next = visible.length - 1;
      visible[next].focus();
    }
    chips.forEach(function (c) {
      c.addEventListener("keydown", onChipKeydown);
    });

    // ─── Por ocasión sub-picker (Decision §D2) ──────────────────────────

    var occasionPicker = null;
    var occasionChip = root.querySelector('[data-filter-value="por-ocasion"]');

    function buildOccasionPicker() {
      if (!occasionChip) return null;
      var picker = document.createElement("div");
      picker.className = "wishlist-toolbar__occasion-picker";
      picker.setAttribute("role", "menu");
      picker.setAttribute(
        "aria-label",
        (localeCarrier &&
          localeCarrier.getAttribute("data-occasion-label")) ||
          "Ocasión"
      );
      picker.setAttribute("hidden", "");
      occasionChip.insertAdjacentElement("afterend", picker);
      return picker;
    }

    function openOccasionPicker() {
      if (!occasionPicker) occasionPicker = buildOccasionPicker();
      if (!occasionPicker) return;
      var products = safeReadProducts();
      var looks = safeReadLooks();
      var setOcc = {};
      products.forEach(function (p) {
        if (p && p.occasion) setOcc[p.occasion] = true;
      });
      looks.forEach(function (l) {
        if (l && l.occasion) setOcc[l.occasion] = true;
      });
      var occasions = Object.keys(setOcc);
      occasionPicker.innerHTML = "";
      var allLabel =
        (localeCarrier &&
          localeCarrier.getAttribute("data-occasion-all")) ||
        "Todas";
      var btnAll = document.createElement("button");
      btnAll.type = "button";
      btnAll.className = "wishlist-toolbar__occasion-option";
      btnAll.setAttribute("role", "menuitemradio");
      btnAll.setAttribute(
        "aria-checked",
        state.activeOccasion ? "false" : "true"
      );
      btnAll.textContent = allLabel;
      btnAll.addEventListener("click", function () {
        state.activeOccasion = null;
        persistFilter(state.filterId, null);
        emitFilterChanged();
        closeOccasionPicker();
      });
      occasionPicker.appendChild(btnAll);
      occasions.forEach(function (occ) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "wishlist-toolbar__occasion-option";
        btn.setAttribute("role", "menuitemradio");
        btn.setAttribute(
          "aria-checked",
          state.activeOccasion === occ ? "true" : "false"
        );
        btn.textContent = occ;
        btn.addEventListener("click", function () {
          state.activeOccasion = occ;
          persistFilter("por-ocasion", occ);
          emitFilterChanged();
          closeOccasionPicker();
          updateChipLabel();
        });
        occasionPicker.appendChild(btn);
      });
      occasionPicker.removeAttribute("hidden");
    }

    function closeOccasionPicker() {
      if (occasionPicker) occasionPicker.setAttribute("hidden", "");
    }

    function updateChipLabel() {
      if (!occasionChip) return;
      var labelEl = occasionChip.querySelector(".benvel-filter-chip__label");
      if (!labelEl) return;
      var base =
        (localeCarrier &&
          localeCarrier.getAttribute("data-chip-por-ocasion")) ||
        "Por ocasión";
      labelEl.textContent = state.activeOccasion
        ? base + " · " + state.activeOccasion
        : base;
    }

    // Click-outside / ESC closes the occasion picker.
    document.addEventListener("click", function (e) {
      if (!occasionPicker || occasionPicker.hasAttribute("hidden")) return;
      if (occasionPicker.contains(e.target)) return;
      if (occasionChip && occasionChip.contains(e.target)) return;
      closeOccasionPicker();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeOccasionPicker();
    });

    // ─── Tab-awareness — hide "Disponibles" on Looks tab ────────────────

    function syncTabAwareness() {
      var activeTab = "piezas";
      var tab = document.querySelector('[data-bv-wishlist-tab][aria-selected="true"]');
      if (tab) activeTab = tab.getAttribute("data-bv-wishlist-tab") || "piezas";
      var disp = root.querySelector('[data-filter-value="disponibles"]');
      if (!disp) return;
      if (activeTab === "looks") {
        disp.setAttribute("hidden", "");
        if (state.filterId === "disponibles") {
          activateFilter("recientes", { openPicker: false });
        }
      } else {
        disp.removeAttribute("hidden");
      }
    }

    document.addEventListener("click", function (e) {
      var t = e.target;
      while (t && t !== document.body) {
        if (t.getAttribute && t.getAttribute("data-bv-wishlist-tab")) {
          // Tab clicked — defer until aria-selected mutates.
          window.setTimeout(syncTabAwareness, 0);
          return;
        }
        t = t.parentNode;
      }
    });

    // ─── Share dialog ───────────────────────────────────────────────────

    var share = document.querySelector("[data-bv-wishlist-share]");
    if (share) {
      var toggle = share.querySelector("[data-bv-wishlist-share-toggle]");
      var dialog = share.querySelector("[data-bv-wishlist-share-dialog]");
      var closeBtn = share.querySelector("[data-bv-wishlist-share-close]");
      var intentButtons = Array.prototype.slice.call(
        share.querySelectorAll("[data-share-intent]")
      );
      var lastFocus = null;

      function openDialog() {
        if (!dialog || !toggle) return;
        lastFocus = document.activeElement;
        dialog.removeAttribute("hidden");
        toggle.setAttribute("aria-expanded", "true");
        // Initial focus on first intent — APG dialog pattern.
        if (intentButtons[0]) intentButtons[0].focus();
        document.addEventListener("keydown", onDialogKeydown);
      }

      function closeDialog() {
        if (!dialog || !toggle) return;
        dialog.setAttribute("hidden", "");
        toggle.setAttribute("aria-expanded", "false");
        document.removeEventListener("keydown", onDialogKeydown);
        if (lastFocus && typeof lastFocus.focus === "function") {
          lastFocus.focus();
        }
      }

      function onDialogKeydown(e) {
        if (e.key === "Escape") {
          e.preventDefault();
          closeDialog();
          return;
        }
        if (e.key !== "Tab") return;
        // Simple focus trap — cycle between intent buttons + close button.
        var focusable = intentButtons.concat(closeBtn ? [closeBtn] : []);
        if (focusable.length === 0) return;
        var active = document.activeElement;
        var idx = focusable.indexOf(active);
        if (e.shiftKey) {
          if (idx <= 0) {
            e.preventDefault();
            focusable[focusable.length - 1].focus();
          }
        } else {
          if (idx === focusable.length - 1) {
            e.preventDefault();
            focusable[0].focus();
          }
        }
      }

      if (toggle) toggle.addEventListener("click", function () {
        if (toggle.getAttribute("aria-expanded") === "true") {
          closeDialog();
        } else {
          openDialog();
        }
      });
      if (closeBtn) closeBtn.addEventListener("click", closeDialog);
      dialog && dialog.addEventListener("click", function (e) {
        if (e.target === dialog) closeDialog();
      });

      intentButtons.forEach(function (btn) {
        btn.addEventListener("click", function () {
          var intent = btn.getAttribute("data-share-intent");
          handleShare(intent);
          closeDialog();
        });
      });

      function handleShare(intent) {
        var products = safeReadProducts();
        var looks = safeReadLooks();
        var filtered = applyFilter(products, looks, state);
        var msgTemplate = t["message_" + intent.replace(/-/g, "_")];
        if (!msgTemplate) return;
        var items = buildItemLines(t, filtered.products, filtered.looks);
        if (!items) items = t.empty_message;
        var total = formatCOP(sumTotals(filtered.products, filtered.looks));
        var body = msgTemplate
          .replace("{items}", items)
          .replace("{total}", total);

        var waNumber = share.getAttribute("data-wa-number") || "";
        if (!waNumber) return;
        var url =
          "https://wa.me/" +
          encodeURIComponent(waNumber) +
          "?text=" +
          encodeURIComponent(body);

        // First-party user navigation — always allowed (the buyer explicitly
        // requested the share). Analytics emission below is the only thing
        // gated by consent (AC4).
        try {
          window.open(url, "_blank", "noopener,noreferrer");
        } catch (_e) {
          /* Pop-up blocked — degrade silently; the buyer can re-tap. */
        }

        if (hasAnalyticsConsent()) {
          try {
            document.dispatchEvent(
              new CustomEvent("benvel:wishlist-shared", {
                bubbles: true,
                detail: {
                  intent: intent,
                  itemCount: filtered.products.length,
                  lookCount: filtered.looks.length,
                  activeFilter: state.filterId,
                  sourceSurface: "wishlist-page",
                },
              })
            );
          } catch (_e) {
            /* CustomEvent unavailable — silent. */
          }
        }
      }
    }

    // ─── Boot ───────────────────────────────────────────────────────────

    setChipPressed(state.filterId);
    syncTabAwareness();
    // Emit the initial filter state so any grid listener can pick up the
    // persisted selection on page load.
    emitFilterChanged();
  }

  window.benvel = window.benvel || {};
  window.benvel.wishlistToolbar = { __loaded: true };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();

/* ──────────────────────────────────────────────────────────────────────────
 * Story 14.5 — Unavailable-state matrix submodule.
 *
 * Owns the runtime behavior for the action row beneath each saved-product
 * card. The state-matrix render itself lives in:
 *   - `snippets/wishlist-saved-product-card-actions.liquid` (canonical)
 *   - `<template id="bv-wishlist-card-actions-template">` host
 *     in `sections/benvel-wishlist-page.liquid` (cloned by the page's
 *     hydration JS, which runs the same precedence in JS).
 *
 * This submodule adds:
 *   1. `data-bv-bis-trigger` handler — "Avisarme cuando vuelva" CTA
 *      delegates to the installed back-in-stock app's contract
 *      (`<bis-trigger>`, `window.bisInstance`, or `data-bis-product-id`
 *      attribute pattern). When no app contract is present at click
 *      time, the handler degrades to a pre-composed mailto: open
 *      (D5 graceful-degradation). Emits the consent-gated analytics
 *      event `benvel:wishlist-back-in-stock-requested` once the
 *      submission has either fired or fallback opened.
 *
 *   2. `data-bv-wishlist-similar` enhancer — when the buyer clicks a
 *      "Ver similares" link AND there is no
 *      `product.metafields.benvel.alternative_collection_handle`
 *      (data-bv-wishlist-has-alt="false"), and the Stylist surface
 *      is present (`window.benvel.stylist` namespace from Epic 18 OR
 *      a `/pages/asesor-de-estilo` route returns 200), the click is
 *      intercepted and routed to
 *      `/pages/asesor-de-estilo?source=wishlist-similar&product_id=…`
 *      instead of the graceful `/collections/all?q=…` fallback the
 *      anchor href carries. We use the URL escalation pattern rather
 *      than runtime route probing — it preserves no-JS / right-click
 *      "open in new tab" behavior on the graceful link.
 *
 * Consent-gated analytics:
 *   - `benvel:wishlist-back-in-stock-requested` — fires ONLY when
 *     `window.benvel.consent?.has?.('analytics') === true`. Payload
 *     is opaque IDs only (productId, variantId, sourcePage). Story
 *     8.1 PII denylist applies. The functional submission (mailto
 *     fallback or app trigger) is NEVER gated — the buyer's action
 *     completes regardless of analytics consent state.
 *
 * Idempotent — the submodule attaches one delegated listener at the
 * document root so DOMContentLoaded ordering is irrelevant. ChipGroup
 * re-renders + storage hydration cycles re-target attributes on the
 * existing nodes; no listener cleanup needed.
 * ────────────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  if (
    window.benvel &&
    window.benvel.wishlistUnavailable &&
    window.benvel.wishlistUnavailable.__loaded
  ) {
    return;
  }

  /* ─── Consent helper (mirror of the Story 14.1 kernel hook) ─────────── */
  function hasAnalyticsConsent() {
    var consent =
      window.benvel && window.benvel.consent ? window.benvel.consent : null;
    return !!(consent && typeof consent.has === "function" && consent.has("analytics"));
  }

  function emitBackInStockRequested(detail) {
    if (!hasAnalyticsConsent()) return;
    document.dispatchEvent(
      new CustomEvent("benvel:wishlist-back-in-stock-requested", {
        bubbles: true,
        detail: {
          productId: detail.productId,
          variantId: detail.variantId,
          sourcePage: detail.sourcePage || "wishlist",
        },
      })
    );
  }

  /* ─── Back-in-stock app discovery ───────────────────────────────────── */
  /* Reads the installed back-in-stock app's contract. Three patterns
   * are recognized — each maps to a real app integration:
   *   (a) `<bis-trigger>` custom element with `.open()` — Klaviyo
   *        Back-in-Stock app's web-component pattern.
   *   (b) `window.bisInstance` or `window.Klaviyo` namespace with
   *        `.openForm(variantId)` — older Klaviyo wrapper.
   *   (c) `data-bis-product-id` data-attribute on a hidden anchor
   *        that the app's bootstrap binds — generic fallback.
   * Returns a function that opens the app for the given variantId,
   * or null when no contract is found. */
  function discoverBisOpener(variantId, productHandle) {
    /* (a) Web component */
    var component = document.querySelector(
      "bis-trigger, [data-back-in-stock-trigger]"
    );
    if (component && typeof component.open === "function") {
      return function () {
        component.setAttribute("data-variant-id", String(variantId));
        component.open();
      };
    }
    /* (b) Klaviyo wrapper */
    if (window.bisInstance && typeof window.bisInstance.openForm === "function") {
      return function () { window.bisInstance.openForm(variantId); };
    }
    if (window.Klaviyo && typeof window.Klaviyo.openForm === "function") {
      return function () { window.Klaviyo.openForm(variantId); };
    }
    /* (c) data-attribute fallback — synthetic click on a hidden anchor */
    var anchor = document.querySelector(
      'a[data-bis-product-id="' + productHandle + '"]'
    );
    if (anchor && typeof anchor.click === "function") {
      return function () { anchor.click(); };
    }
    return null;
  }

  function openMailtoFallback(button) {
    var shopEmail = (window.benvel && window.benvel.shop && window.benvel.shop.email)
      || "contacto@benvel.co";
    var productTitle = button.getAttribute("data-bv-bis-product-title") || "";
    var productHandle = button.getAttribute("data-bv-bis-product-handle") || "";
    var subject = encodeURIComponent(
      "Avisarme cuando vuelva: " + productTitle
    );
    var body = encodeURIComponent(
      "Hola Benvel, quiero que me avisen cuando vuelva esta pieza: " +
      productTitle +
      " (https://" + window.location.host + "/products/" + productHandle + ")."
    );
    var href = "mailto:" + shopEmail + "?subject=" + subject + "&body=" + body;
    /* Open in the same tab — mailto: doesn't actually navigate, so the
     * buyer stays on /pages/favoritos. */
    window.location.href = href;
  }

  function onBisTriggerClick(event) {
    var button = event.target.closest("[data-bv-bis-trigger]");
    if (!button) return;
    var variantId = button.getAttribute("data-bv-bis-trigger");
    var productHandle = button.getAttribute("data-bv-bis-product-handle") || "";
    var productId = button.getAttribute("data-bv-wishlist-product-id")
      || (button.closest("[data-bv-wishlist-product-id]")
            && button.closest("[data-bv-wishlist-product-id]").getAttribute("data-bv-wishlist-product-id"))
      || "";
    if (!variantId) return;
    event.preventDefault();

    var open = discoverBisOpener(variantId, productHandle);
    if (open) {
      try {
        open();
      } catch (_e) {
        /* App contract present but threw — fall back to mailto. */
        openMailtoFallback(button);
      }
    } else {
      openMailtoFallback(button);
    }
    emitBackInStockRequested({
      productId: productId,
      variantId: variantId,
      sourcePage: button.getAttribute("data-bv-bis-source-page") || "wishlist",
    });
  }

  /* ─── "Ver similares" Stylist escalation ────────────────────────────── */
  /* When the link carries `data-bv-wishlist-has-alt="false"` and the
   * AI Stylist surface namespace is present at click time (Epic 18 has
   * shipped a `window.benvel.stylist` consumer), we route the click to
   * the Stylist No-Match Recommendations surface instead of the
   * graceful collections/all search fallback. The anchor's href
   * preserves the graceful fallback for no-JS / right-click cases. */
  function onSimilarLinkClick(event) {
    var link = event.target.closest("[data-bv-wishlist-similar]");
    if (!link) return;
    /* Only intercept the modifier-less left click — preserve open-in-new-tab. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    var hasAlt = link.getAttribute("data-bv-wishlist-has-alt") === "true";
    if (hasAlt) return; // Path A — graceful link is already the right destination.

    var hasStylist =
      window.benvel &&
      window.benvel.stylist &&
      typeof window.benvel.stylist.openNoMatch === "function";
    if (!hasStylist) return; // Path B′ graceful fallback stands.

    event.preventDefault();
    var productId = link.getAttribute("data-bv-wishlist-product-id") || "";
    try {
      window.benvel.stylist.openNoMatch({
        source: "wishlist-similar",
        productId: productId,
      });
    } catch (_e) {
      /* Stylist namespace blew up — let the graceful link navigate. */
      window.location.href = link.getAttribute("href");
    }
  }

  document.addEventListener("click", onBisTriggerClick);
  document.addEventListener("click", onSimilarLinkClick);

  window.benvel = window.benvel || {};
  window.benvel.wishlistUnavailable = {
    __loaded: true,
    /* Exposed for test harnesses + future Quick View consumers. */
    discoverBisOpener: discoverBisOpener,
    emitBackInStockRequested: emitBackInStockRequested,
  };
})();

